/**
 * AI Insights Engine - Pattern-based analytics insights generation
 *
 * Uses statistical rules to identify patterns, anomalies, and recommendations
 * from survey response data. No external API required - all processing is local.
 */

export interface Insight {
  id: string;
  type: 'trend' | 'anomaly' | 'correlation' | 'recommendation' | 'summary';
  priority: 'high' | 'medium' | 'low';
  title: string;
  description: string;
  icon: 'AlertTriangle' | 'TrendingUp' | 'CheckCircle' | 'Info' | 'Zap' | 'Target' | 'BarChart';
  questionId?: string;
  questionTitle?: string;
}

export interface QuestionAnalyticsInput {
  questionId: string;
  questionTitle: string;
  questionType: string;
  responseCount: number;
  totalSurveyRespondents: number;
  eligibleRespondents?: number;
  skipCount?: number;
  distribution?: Record<string, {
    count: number;
    percentOfAnswered: number;
    percentOfTotal: number;
  }>;
  numericStats?: {
    min: number;
    max: number;
    average: number;
    median: number;
    stdDev?: number;
    mode?: number;
  };
  textResponses?: Array<{
    text: string;
    wordCount: number;
    responseIndex: number;
  }>;
}

/**
 * Generate insights from survey analytics data
 */
export function generateInsights(
  analytics: QuestionAnalyticsInput[],
  totalResponses: number
): Insight[] {
  const insights: Insight[] = [];
  let insightId = 0;

  const createId = () => `insight-${++insightId}`;

  // Survey-level insights
  if (totalResponses > 0) {
    // Check overall completion
    const avgResponseRate = analytics.reduce((sum, q) => {
      const eligible = q.eligibleRespondents ?? q.totalSurveyRespondents;
      return sum + (eligible > 0 ? (q.responseCount / eligible) * 100 : 0);
    }, 0) / analytics.length;

    if (avgResponseRate >= 90) {
      insights.push({
        id: createId(),
        type: 'summary',
        priority: 'low',
        title: 'Excellent Response Rate',
        description: `Survey achieved ${avgResponseRate.toFixed(0)}% average response rate across all questions. Data quality is high.`,
        icon: 'CheckCircle',
      });
    }
  }

  // Question-level insights
  analytics.forEach((q) => {
    const eligible = q.eligibleRespondents ?? q.totalSurveyRespondents;
    const responseRate = eligible > 0 ? (q.responseCount / eligible) * 100 : 0;
    const skipRate = q.skipCount && eligible > 0 ? (q.skipCount / eligible) * 100 : 0;

    // ============================================
    // 1. RESPONSE RATE INSIGHTS
    // ============================================

    // Low response rate warning
    if (responseRate < 50 && eligible >= 5) {
      insights.push({
        id: createId(),
        type: 'anomaly',
        priority: 'high',
        title: 'Low Response Rate',
        description: `Only ${responseRate.toFixed(0)}% answered "${q.questionTitle}". Consider if the question is confusing, sensitive, or placed poorly in the survey.`,
        icon: 'AlertTriangle',
        questionId: q.questionId,
        questionTitle: q.questionTitle,
      });
    }

    // High skip rate
    if (skipRate > 30 && q.skipCount && q.skipCount >= 3) {
      insights.push({
        id: createId(),
        type: 'anomaly',
        priority: 'medium',
        title: 'High Skip Rate',
        description: `${skipRate.toFixed(0)}% skipped "${q.questionTitle}". Respondents may be reluctant to answer or finding it irrelevant.`,
        icon: 'AlertTriangle',
        questionId: q.questionId,
        questionTitle: q.questionTitle,
      });
    }

    // ============================================
    // 2. DISTRIBUTION INSIGHTS (Choice Questions)
    // ============================================

    if (q.distribution && Object.keys(q.distribution).length > 0) {
      const entries = Object.entries(q.distribution);
      const sortedByCount = [...entries].sort(([, a], [, b]) => b.count - a.count);
      const topOption = sortedByCount[0];
      const topPercent = topOption?.[1]?.percentOfAnswered || 0;

      // Dominant response (consensus or bias)
      if (topPercent > 70 && q.responseCount >= 5) {
        insights.push({
          id: createId(),
          type: 'trend',
          priority: 'medium',
          title: 'Strong Consensus',
          description: `${topPercent.toFixed(0)}% selected "${topOption[0]}" for "${q.questionTitle}". This indicates strong agreement or potential response bias.`,
          icon: 'TrendingUp',
          questionId: q.questionId,
          questionTitle: q.questionTitle,
        });
      }

      // Even distribution (possible random responding)
      if (entries.length >= 4 && q.responseCount >= 10) {
        const expectedPercent = 100 / entries.length;
        const isUniform = entries.every(([, data]) =>
          Math.abs(data.percentOfAnswered - expectedPercent) < 15
        );
        if (isUniform) {
          insights.push({
            id: createId(),
            type: 'anomaly',
            priority: 'low',
            title: 'Uniform Distribution',
            description: `Responses to "${q.questionTitle}" are evenly spread across options. This may indicate random responding or genuine variety.`,
            icon: 'Info',
            questionId: q.questionId,
            questionTitle: q.questionTitle,
          });
        }
      }

      // Bimodal distribution (polarized opinions)
      if (entries.length >= 3 && q.responseCount >= 10) {
        const sorted = [...entries].sort(([, a], [, b]) => b.count - a.count);
        if (sorted.length >= 3) {
          const top2Sum = (sorted[0][1].percentOfAnswered || 0) + (sorted[1][1].percentOfAnswered || 0);
          const middlePercent = sorted[2][1].percentOfAnswered || 0;
          if (top2Sum > 70 && middlePercent < 15 &&
              Math.abs((sorted[0][1].percentOfAnswered || 0) - (sorted[1][1].percentOfAnswered || 0)) < 20) {
            insights.push({
              id: createId(),
              type: 'trend',
              priority: 'medium',
              title: 'Polarized Responses',
              description: `"${q.questionTitle}" shows polarized opinions with most responses split between two options.`,
              icon: 'Zap',
              questionId: q.questionId,
              questionTitle: q.questionTitle,
            });
          }
        }
      }

      // Yes/No specific insights
      if (q.questionType === 'yesno') {
        const yesPercent = q.distribution['Yes']?.percentOfAnswered || 0;
        const noPercent = q.distribution['No']?.percentOfAnswered || 0;

        if (yesPercent > 80 && q.responseCount >= 5) {
          insights.push({
            id: createId(),
            type: 'trend',
            priority: 'medium',
            title: 'High Affirmative Rate',
            description: `${yesPercent.toFixed(0)}% answered Yes to "${q.questionTitle}" - strong positive indication.`,
            icon: 'CheckCircle',
            questionId: q.questionId,
            questionTitle: q.questionTitle,
          });
        } else if (noPercent > 80 && q.responseCount >= 5) {
          insights.push({
            id: createId(),
            type: 'trend',
            priority: 'medium',
            title: 'High Negative Rate',
            description: `${noPercent.toFixed(0)}% answered No to "${q.questionTitle}" - strong negative indication.`,
            icon: 'AlertTriangle',
            questionId: q.questionId,
            questionTitle: q.questionTitle,
          });
        }
      }
    }

    // ============================================
    // 3. NUMERIC INSIGHTS
    // ============================================

    if (q.numericStats && q.responseCount >= 3) {
      const { min, max, average, median, stdDev } = q.numericStats;
      const range = max - min;

      // High variability
      if (stdDev && range > 0 && stdDev > range * 0.35) {
        insights.push({
          id: createId(),
          type: 'summary',
          priority: 'low',
          title: 'High Variability',
          description: `Wide spread in responses to "${q.questionTitle}" (SD: ${stdDev.toFixed(2)}) suggests diverse participant experiences.`,
          icon: 'Zap',
          questionId: q.questionId,
          questionTitle: q.questionTitle,
        });
      }

      // Low variability (consensus)
      if (stdDev && range > 0 && stdDev < range * 0.15 && q.responseCount >= 5) {
        insights.push({
          id: createId(),
          type: 'summary',
          priority: 'low',
          title: 'Strong Agreement',
          description: `Responses to "${q.questionTitle}" cluster tightly (SD: ${stdDev.toFixed(2)}), indicating consensus.`,
          icon: 'Target',
          questionId: q.questionId,
          questionTitle: q.questionTitle,
        });
      }

      // Skewness detection (mean vs median)
      if (stdDev && stdDev > 0) {
        const skewness = (average - median) / stdDev;
        if (Math.abs(skewness) > 0.5) {
          const direction = skewness > 0 ? 'right (higher values)' : 'left (lower values)';
          insights.push({
            id: createId(),
            type: 'trend',
            priority: 'low',
            title: 'Skewed Distribution',
            description: `"${q.questionTitle}" is skewed ${direction}. Outliers may be affecting the average (Mean: ${average.toFixed(1)}, Median: ${median.toFixed(1)}).`,
            icon: 'TrendingUp',
            questionId: q.questionId,
            questionTitle: q.questionTitle,
          });
        }
      }

      // Rating/satisfaction warnings
      if (['rating', 'scale'].includes(q.questionType)) {
        // Assuming 1-5 or 1-10 scale
        const midpoint = (max + min) / 2;
        if (average < midpoint && q.responseCount >= 5) {
          insights.push({
            id: createId(),
            type: 'recommendation',
            priority: 'high',
            title: 'Below Average Rating',
            description: `Average ${average.toFixed(1)} for "${q.questionTitle}" is below the midpoint (${midpoint.toFixed(1)}). This area may need attention.`,
            icon: 'AlertTriangle',
            questionId: q.questionId,
            questionTitle: q.questionTitle,
          });
        } else if (average >= midpoint + (range * 0.3) && q.responseCount >= 5) {
          insights.push({
            id: createId(),
            type: 'summary',
            priority: 'low',
            title: 'High Rating',
            description: `Average ${average.toFixed(1)} for "${q.questionTitle}" indicates positive sentiment.`,
            icon: 'CheckCircle',
            questionId: q.questionId,
            questionTitle: q.questionTitle,
          });
        }
      }

      // Outlier detection
      if (stdDev && stdDev > 0) {
        const lowerBound = average - 2 * stdDev;
        const upperBound = average + 2 * stdDev;
        if (min < lowerBound || max > upperBound) {
          insights.push({
            id: createId(),
            type: 'anomaly',
            priority: 'low',
            title: 'Outliers Detected',
            description: `"${q.questionTitle}" contains outlier values (Min: ${min}, Max: ${max}) that may affect statistical interpretation.`,
            icon: 'Info',
            questionId: q.questionId,
            questionTitle: q.questionTitle,
          });
        }
      }
    }

    // ============================================
    // 4. TEXT RESPONSE INSIGHTS
    // ============================================

    if (q.textResponses && q.textResponses.length > 0) {
      const avgWordCount = q.textResponses.reduce((sum, r) => sum + r.wordCount, 0) / q.textResponses.length;

      // Brief responses
      if (avgWordCount < 5 && q.textResponses.length >= 3) {
        insights.push({
          id: createId(),
          type: 'summary',
          priority: 'low',
          title: 'Brief Responses',
          description: `"${q.questionTitle}" has short answers (avg ${avgWordCount.toFixed(1)} words). Consider if more detailed responses are needed.`,
          icon: 'Info',
          questionId: q.questionId,
          questionTitle: q.questionTitle,
        });
      }

      // Detailed responses (positive engagement)
      if (avgWordCount > 30 && q.textResponses.length >= 3) {
        insights.push({
          id: createId(),
          type: 'summary',
          priority: 'low',
          title: 'Detailed Responses',
          description: `"${q.questionTitle}" received detailed answers (avg ${avgWordCount.toFixed(1)} words), indicating high engagement.`,
          icon: 'CheckCircle',
          questionId: q.questionId,
          questionTitle: q.questionTitle,
        });
      }

      // Response length variability
      if (q.textResponses.length >= 5) {
        const wordCounts = q.textResponses.map(r => r.wordCount);
        const maxWords = Math.max(...wordCounts);
        const minWords = Math.min(...wordCounts);
        if (maxWords > minWords * 10 && maxWords > 50) {
          insights.push({
            id: createId(),
            type: 'summary',
            priority: 'low',
            title: 'Variable Response Lengths',
            description: `"${q.questionTitle}" shows high variability in response length (${minWords}-${maxWords} words), suggesting different engagement levels.`,
            icon: 'BarChart',
            questionId: q.questionId,
            questionTitle: q.questionTitle,
          });
        }
      }
    }
  });

  // Sort insights by priority (high > medium > low)
  const priorityOrder: Record<string, number> = { high: 0, medium: 1, low: 2 };
  insights.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);

  return insights;
}

/**
 * Get color for insight priority
 */
export function getInsightPriorityColor(priority: Insight['priority']): {
  bg: string;
  border: string;
  text: string;
  icon: string;
} {
  switch (priority) {
    case 'high':
      return {
        bg: 'rgba(220, 38, 38, 0.08)',
        border: '#DC2626',
        text: '#991B1B',
        icon: '#DC2626',
      };
    case 'medium':
      return {
        bg: 'rgba(245, 158, 11, 0.08)',
        border: '#F59E0B',
        text: '#92400E',
        icon: '#F59E0B',
      };
    case 'low':
    default:
      return {
        bg: 'rgba(38, 67, 114, 0.08)',
        border: '#264372',
        text: '#1E3A5F',
        icon: '#264372',
      };
  }
}

/**
 * Get icon component for insight
 */
export function getInsightIcon(iconName: Insight['icon']): string {
  // Return SVG path data for each icon type
  const icons: Record<Insight['icon'], string> = {
    AlertTriangle: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-1.964-1.333-2.732 0L3.34 16c-.77 1.333.192 3 1.732 3z',
    TrendingUp: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6',
    CheckCircle: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
    Info: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
    Zap: 'M13 10V3L4 14h7v7l9-11h-7z',
    Target: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
    BarChart: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
  };
  return icons[iconName] || icons.Info;
}
